import { queryOptions, useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";

import type { QueryConfig } from "@/lib/react-query";

export type CarMake = {
  make: string;
};

export type CarFamily = {
  make: string;
  family: string;
};

export type CarGeneration = {
  make: string;
  family: string;
  generation: string;
};

export type CarModel = {
  make: string;
  family: string;
  generation: string;
  model: string;
};

export type CarVariant = {
  make: string;
  family: string;
  generation: string;
  model: string;
  variant?: string;
  taxonomy_id: string;
  start_of_production?: number;
  end_of_production?: number;
};

export type CarsModelsApiResponse<T> = {
  results: T[];
};

export const getCarMakes = async (): Promise<CarMake[]> => {
  const response = (await api.get("/carburate/cars/models")) as CarsModelsApiResponse<CarMake>;
  return response.results;
};

export const getCarFamilies = async ({ make }: { make: string }): Promise<CarFamily[]> => {
  const response = (await api.get("/carburate/cars/models", {
    params: { make },
  })) as CarsModelsApiResponse<CarFamily>;
  return response.results;
};

export const getCarGenerations = async ({
  make,
  family,
}: {
  make: string;
  family: string;
}): Promise<CarGeneration[]> => {
  const response = (await api.get("/carburate/cars/models", {
    params: { make, family },
  })) as CarsModelsApiResponse<CarGeneration>;
  return response.results;
};

export const getCarModels = async ({
  make,
  family,
  generation,
}: {
  make: string;
  family: string;
  generation: string;
}): Promise<CarModel[]> => {
  const response = (await api.get("/carburate/cars/models", {
    params: { make, family, generation },
  })) as CarsModelsApiResponse<CarModel>;
  return response.results;
};

export const getCarVariants = async ({
  make,
  family,
  generation,
  model,
}: {
  make: string;
  family: string;
  generation: string;
  model: string;
}): Promise<CarVariant[]> => {
  const response = (await api.get("/carburate/cars/models", {
    params: { make, family, generation, model },
  })) as CarsModelsApiResponse<CarVariant>;
  return response.results;
};

export const getCarMakesQueryOptions = () => {
  return queryOptions({
    queryKey: ["cars", "makes"],
    queryFn: () => getCarMakes(),
  });
};

export const getCarFamiliesQueryOptions = (make: string) => {
  return queryOptions({
    queryKey: ["cars", "families", make],
    queryFn: () => getCarFamilies({ make }),
  });
};

export const getCarGenerationsQueryOptions = (make: string, family: string) => {
  return queryOptions({
    queryKey: ["cars", "generations", make, family],
    queryFn: () => getCarGenerations({ make, family }),
  });
};

export const getCarModelsQueryOptions = (make: string, family: string, generation: string) => {
  return queryOptions({
    queryKey: ["cars", "models", make, family, generation],
    queryFn: () => getCarModels({ make, family, generation }),
  });
};

export const getCarVariantsQueryOptions = (
  make: string,
  family: string,
  generation: string,
  model: string
) => {
  return queryOptions({
    queryKey: ["cars", "variants", make, family, generation, model],
    queryFn: () => getCarVariants({ make, family, generation, model }),
  });
};

export const useCarMakes = (queryConfig?: QueryConfig<typeof getCarMakes>) => {
  return useQuery({
    enabled: hasAccessToken(),
    ...getCarMakesQueryOptions(),
    ...queryConfig,
  });
};

type UseCarFamiliesOptions = {
  make: string;
  queryConfig?: QueryConfig<typeof getCarFamilies>;
};

export const useCarFamilies = ({ make, queryConfig }: UseCarFamiliesOptions) => {
  return useQuery({
    enabled: hasAccessToken() && !!make,
    ...getCarFamiliesQueryOptions(make),
    ...queryConfig,
  });
};

type UseCarGenerationsOptions = {
  make: string;
  family: string;
  queryConfig?: QueryConfig<typeof getCarGenerations>;
};

export const useCarGenerations = ({ make, family, queryConfig }: UseCarGenerationsOptions) => {
  return useQuery({
    enabled: hasAccessToken() && !!make && !!family,
    ...getCarGenerationsQueryOptions(make, family),
    ...queryConfig,
  });
};

type UseCarModelsOptions = {
  make: string;
  family: string;
  generation: string;
  queryConfig?: QueryConfig<typeof getCarModels>;
};

export const useCarModels = ({ make, family, generation, queryConfig }: UseCarModelsOptions) => {
  return useQuery({
    enabled: hasAccessToken() && !!make && !!family && !!generation,
    ...getCarModelsQueryOptions(make, family, generation),
    ...queryConfig,
  });
};

type UseCarVariantsOptions = {
  make: string;
  family: string;
  generation: string;
  model: string;
  queryConfig?: QueryConfig<typeof getCarVariants>;
};

export const useCarVariants = ({
  make,
  family,
  generation,
  model,
  queryConfig,
}: UseCarVariantsOptions) => {
  return useQuery({
    enabled: hasAccessToken() && !!make && !!family && !!generation && !!model,
    ...getCarVariantsQueryOptions(make, family, generation, model),
    ...queryConfig,
  });
};
